/**
 * Turns one AI customization audit row into the spans a trace explorer draws.
 * Older rows have no per-step timing; those spans stay in call order and the
 * UI must not invent durations for them.
 */

const KIND_LABELS = {
  turn: 'TURN',
  model: 'MODEL',
  tool: 'TOOL',
  db: 'DB',
};

function finite(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function classifyTool(name) {
  const text = String(name || '').toLowerCase();
  if (/^(load_|query_|search_|db_|resolve_|visible_)/.test(text) || text.includes('database')) return 'db';
  return 'tool';
}

function clip(value, max = 4000) {
  if (value == null || value === '') return null;
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

function actionInput(action) {
  return clip(action?.inputPreview || action?.description || action?.targetSelector || action?.pagePath || null);
}

function actionOutput(action) {
  if (!action) return null;
  if (action.error) return clip(action.error);
  if (action.evidenceReason) return clip(action.evidenceReason);
  if (action.success === false) return 'failed';
  if (action.description && action.inputPreview) return clip(action.description);
  return action.success === false ? 'failed' : 'ok';
}

function modelInput(span, index) {
  if (span?.inputTokens) return `Round ${index + 1}\ninput ${Number(span.inputTokens).toLocaleString()} tokens`;
  return `Round ${index + 1}`;
}

function modelOutput(span) {
  const lines = [];
  if (span?.stopReason) lines.push(`stop: ${span.stopReason}`);
  if (span?.outputTokens) lines.push(`output ${Number(span.outputTokens).toLocaleString()} tokens`);
  if (span?.error) lines.push(span.error);
  return lines.length ? lines.join('\n') : (span?.success === false ? 'failed' : 'ok');
}

function buildTrace(audit) {
  const actions = Array.isArray(audit?.actions_summary) ? audit.actions_summary : [];
  const meta = audit?.metadata && typeof audit.metadata === 'object' ? audit.metadata : {};
  const modelSpans = Array.isArray(meta.modelSpans) ? meta.modelSpans : [];
  const usage = meta.usage && typeof meta.usage === 'object' ? meta.usage : null;
  const totalMs = finite(audit?.duration_ms);
  const surfaceName = audit?.surface === 'crm_ui_studio'
    ? 'BOS Design Studio'
    : (audit?.advisor_mode ? `${audit.advisor_mode} mode` : 'Website editor');

  const spans = [{
    id: 'turn',
    parentId: null,
    depth: 0,
    kind: 'turn',
    name: surfaceName,
    durationMs: totalMs,
    offsetMs: 0,
    success: audit?.status !== 'failed' && audit?.status !== 'error',
    input: audit?.prompt || null,
    output: audit?.final_response || null,
    meta: {
      status: audit?.status || null,
      outcome: audit?.outcome_label || null,
      provider: audit?.provider || null,
      model: audit?.model || null,
      rounds: audit?.model_rounds_attempted ?? null,
      toolCalls: audit?.tool_calls_count ?? actions.length,
    },
  }];

  const timed = modelSpans.some((span) => finite(span?.offsetMs) != null)
    || actions.some((action) => finite(action?.offsetMs) != null);

  if (timed && (modelSpans.length || actions.length)) {
    const steps = [
      ...modelSpans.map((span, index) => ({
        kind: 'model',
        name: span.name || audit?.model || audit?.provider || 'model',
        durationMs: finite(span.durationMs),
        offsetMs: finite(span.offsetMs) ?? 0,
        success: span.success !== false,
        input: modelInput(span, index),
        output: modelOutput(span),
        meta: span,
        sort: finite(span.offsetMs) ?? index,
      })),
      ...actions.map((action, index) => ({
        kind: classifyTool(action?.action),
        name: action?.action || 'unknown',
        durationMs: finite(action.durationMs),
        offsetMs: finite(action.offsetMs) ?? 0,
        success: action?.success !== false,
        input: actionInput(action),
        output: actionOutput(action),
        meta: action,
        sort: (finite(action.offsetMs) ?? 100000) + index / 1000,
      })),
    ].sort((a, b) => a.sort - b.sort);

    let currentModelId = 'turn';
    steps.forEach((step, index) => {
      const id = `step-${index}`;
      const underModel = step.kind !== 'model' && currentModelId !== 'turn';
      spans.push({
        id,
        parentId: step.kind === 'model' ? 'turn' : currentModelId,
        depth: step.kind === 'model' ? 1 : (underModel ? 2 : 1),
        kind: step.kind,
        name: step.name,
        durationMs: step.durationMs,
        offsetMs: step.offsetMs,
        success: step.success,
        input: step.input,
        output: step.output,
        meta: step.meta,
      });
      if (step.kind === 'model') currentModelId = id;
    });
  } else {
    spans.push({
      id: 'model',
      parentId: 'turn',
      depth: 1,
      kind: 'model',
      name: audit?.model || audit?.provider || 'model',
      durationMs: null,
      offsetMs: null,
      success: true,
      input: audit?.model_rounds_attempted
        ? `${audit.model_rounds_attempted} model round(s) on this turn`
        : 'Model rounds were not recorded individually',
      output: null,
      meta: {
        rounds: audit?.model_rounds_attempted ?? null,
        provider: audit?.provider || null,
        model: audit?.model || null,
      },
    });
    actions.forEach((action, index) => {
      spans.push({
        id: `tool-${index}`,
        parentId: 'model',
        depth: 2,
        kind: classifyTool(action?.action),
        name: action?.action || 'unknown',
        durationMs: finite(action?.durationMs),
        offsetMs: finite(action?.offsetMs),
        success: action?.success !== false,
        input: actionInput(action),
        output: actionOutput(action),
        meta: action,
      });
    });
  }

  const scaleMs = totalMs || spans.reduce((max, span) => {
    const end = (span.offsetMs || 0) + (span.durationMs || 0);
    return Math.max(max, end);
  }, 0) || null;

  const hasTiming = Boolean(timed && scaleMs);

  return { spans, hasTiming, scaleMs, usage };
}

function spanBarBox(span, index, count, hasTiming, scaleMs) {
  if (span.kind === 'turn') {
    return { left: 0, width: 100 };
  }
  if (hasTiming && scaleMs && span.offsetMs != null) {
    const left = Math.min(99, (span.offsetMs / scaleMs) * 100);
    const raw = ((span.durationMs || 0) / scaleMs) * 100;
    const width = Math.min(Math.max(raw, 0.9), 100 - left);
    return { left, width };
  }
  const slot = 100 / Math.max(count, 1);
  const left = index * slot;
  return { left, width: Math.max(slot - 0.5, 0.9) };
}

module.exports = {
  KIND_LABELS,
  classifyTool,
  buildTrace,
  spanBarBox,
};
